import getWindow from 'get-window';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';

import type { BlockRange } from '@gitbook/slate';
import { IS_IOS } from '@gitbook/slate-dev-environment';

import findSlateDOMNode from './find-dom-node';
import { isSelectionBackward } from './isSelectionBackward';

/*
 * CSS overflow values that would cause scrolling.
 */

const OVERFLOWS = ['auto', 'overlay', 'scroll'];

/*
 * Detect whether we are running IOS version 11
 */

const IS_IOS_11 = IS_IOS && !!window.navigator.userAgent.match(/os 11_/i);

/*
 * Find the nearest parent with scrolling, or window.
 */
function findScrollContainer(el: Node, window: Window): HTMLElement {
    let parent = el.parentNode;
    let scroller;

    while (!scroller) {
        if (!parent?.parentNode) {
            break;
        }

        const style = window.getComputedStyle(parent as Element);
        const { overflowY } = style;

        if (OVERFLOWS.includes(overflowY)) {
            scroller = parent;
            break;
        }

        parent = parent.parentNode;
    }

    // COMPAT: Because Chrome does not allow doucment.body.scrollTop, we're
    // assuming that window.scrollTo() should be used if the scrollable element
    // turns out to be document.body or document.documentElement. This will work
    // unless body is intentionally set to scrollable by restricting its height
    // (e.g. height: 100vh).
    if (!scroller) {
        return window.document.body;
    }

    return scroller;
}

/*
 * Scroll the current block selection's focus point into view if needed.
 *
 * For BlockRange we don't have a native selection to work with
 * so we need to scroll to the block's DOM node.
 */
export function scrollToBlockRange(range: BlockRange) {
    const { focusBlock } = range;
    if (!focusBlock) {
        return;
    }
    const focusNode = findSlateDOMNode(focusBlock);
    if (!focusNode) {
        return;
    }
    scrollIntoViewIfNeeded(focusNode, { block: 'nearest' });
}

/*
 * Scroll the current selection's focus point into view if needed.
 */
function scrollToSelection(selection: Selection) {
    if (IS_IOS_11) {
        return;
    }
    if (!selection.anchorNode) {
        return;
    }
    if (selection.rangeCount < 1) {
        return;
    }

    // Slate has patched scrolling to an expanded selection as this
    // was affecting the selection of multiple blocks and dragging behavior:
    // https://github.com/ianstormtaylor/slate/blob/main/packages/slate-react/src/components/editable.tsx#L1693
    if (!selection.isCollapsed) {
        return;
    }

    const range = selection.getRangeAt(0).cloneRange();
    range.collapse(isSelectionBackward(selection));
    const cursorRect = range.getBoundingClientRect();

    scrollToPosition({ rect: cursorRect, node: selection.anchorNode });
}

interface ScrollToPositonProps {
    rect: DOMRect;
    node: Node;
}

/*
 * Update the scroll position of the scrollable container
 */
function scrollToPosition({ rect, node }: ScrollToPositonProps) {
    const window = getWindow(node);
    const scroller = findScrollContainer(node, window);
    const isWindow =
        scroller === window.document.body || scroller === window.document.documentElement;

    let width;
    let height;
    let yOffset;
    let xOffset;
    let scrollerTop = 0;
    let scrollerLeft = 0;
    let scrollerBordersY = 0;
    let scrollerBordersX = 0;
    let scrollerPaddingTop = 0;
    let scrollerPaddingBottom = 0;
    let scrollerPaddingLeft = 0;
    let scrollerPaddingRight = 0;

    if (isWindow) {
        const { innerWidth, innerHeight, pageYOffset, pageXOffset } = window;
        width = innerWidth;
        height = innerHeight;
        yOffset = pageYOffset;
        xOffset = pageXOffset;
    } else {
        const { offsetWidth, offsetHeight, scrollTop, scrollLeft } = scroller;
        const {
            borderTopWidth,
            borderBottomWidth,
            borderLeftWidth,
            borderRightWidth,
            paddingTop,
            paddingBottom,
            paddingLeft,
            paddingRight,
        } = window.getComputedStyle(scroller);

        const scrollerRect = scroller.getBoundingClientRect();
        width = offsetWidth;
        height = offsetHeight;
        scrollerTop = scrollerRect.top + Number.parseInt(borderTopWidth, 10);
        scrollerLeft = scrollerRect.left + Number.parseInt(borderLeftWidth, 10);

        scrollerBordersY =
            Number.parseInt(borderTopWidth, 10) + Number.parseInt(borderBottomWidth, 10);

        scrollerBordersX =
            Number.parseInt(borderLeftWidth, 10) + Number.parseInt(borderRightWidth, 10);

        scrollerPaddingTop = Number.parseInt(paddingTop, 10);
        scrollerPaddingBottom = Number.parseInt(paddingBottom, 10);
        scrollerPaddingLeft = Number.parseInt(paddingLeft, 10);
        scrollerPaddingRight = Number.parseInt(paddingRight, 10);
        yOffset = scrollTop;
        xOffset = scrollLeft;
    }

    const cursorTop = rect.top + yOffset - scrollerTop;
    const cursorLeft = rect.left + xOffset - scrollerLeft;

    let x = xOffset;
    let y = yOffset;

    if (cursorLeft < xOffset) {
        // selection to the left of viewport
        x = cursorLeft - scrollerPaddingLeft;
    } else if (cursorLeft + rect.width + scrollerBordersX > xOffset + width) {
        // selection to the right of viewport
        x = cursorLeft + scrollerBordersX + scrollerPaddingRight - width;
    }

    if (cursorTop < yOffset) {
        // selection above viewport
        y = cursorTop - scrollerPaddingTop;
    } else if (cursorTop + rect.height + scrollerBordersY > yOffset + height) {
        // selection below viewport
        y = cursorTop + scrollerBordersY + scrollerPaddingBottom + rect.height - height;
    }

    if (isWindow) {
        window.scrollTo({ top: 0, left: 0 });
    } else {
        scroller.scrollTop = y;
        scroller.scrollLeft = x;
    }
}

export default scrollToSelection;
